import React, { useState, useEffect, useRef } from "react";
import apiService from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
} from "../arc/components/dialog/dialog";
import { Input } from "../arc/components/input/input";
import { Textarea } from "../arc/components/textarea/textarea";

const FEEDBACK_TYPES = [
  { value: "general", label: "General Feedback" },
  { value: "feature", label: "Feature Request" },
  { value: "bug", label: "Bug / Issue" },
  { value: "pricing", label: "Pricing Question" },
];

const PLACEHOLDERS = {
  general: "Share your thoughts, questions, or feedback…",
  feature: "Describe the feature and how it would help your workflow…",
  bug: "What happened? How do we reproduce it?",
  pricing: "Any questions about plans or pricing?",
};

export default function DemoFeedbackModal() {
  const { user } = useAuth();

  const [open, setOpen] = useState(false);
  const [showTip, setShowTip] = useState(false);
  const [type, setType] = useState("general");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState("idle");

  const panelRef = useRef(null);
  const firstRef = useRef(null);

  // Seed name/email from auth user
  useEffect(() => {
    if (user?.name) setName(user.name);
    if (user?.email) setEmail(user.email);
  }, [user]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target))
        setOpen(false);
    };
    const tid = setTimeout(
      () => document.addEventListener("mousedown", handler),
      50,
    );
    return () => {
      clearTimeout(tid);
      document.removeEventListener("mousedown", handler);
    };
  }, [open]);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  // Focus first field when opening
  useEffect(() => {
    if (open) setTimeout(() => firstRef.current?.focus(), 150);
  }, [open]);

  const canSubmit =
    name.trim() && email.trim() && message.trim() && status === "idle";

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setStatus("sending");
    try {
      await apiService.client.post("/demo/feedback", {
        type,
        name: name.trim(),
        email: email.trim(),
        message: message.trim(),
        companyName: user?.companyName || "",
      });
      setStatus("sent");
    } catch (err) {
      console.error(err);
      setStatus("error");
    }
  };

  const handleDone = () => {
    setOpen(false);
    setTimeout(() => {
      setStatus("idle");
      setMessage("");
      setType("general");
    }, 300);
  };

  const onFocus = (e) => {
    e.target.style.borderColor = "var(--accent)";
    e.target.style.boxShadow = "none";
  };
  const onBlur = (e) => {
    e.target.style.borderColor = "var(--border)";
    e.target.style.boxShadow = "none";
  };

  const fieldStyle = {
    width: "100%",
    padding: "9px 11px",
    borderRadius: 8,
    fontSize: 13,
    border: "1px solid var(--border)",
    background: "var(--surface-raised)",
    color: "var(--text)",
    outline: "none",
    fontFamily: "inherit",
    boxSizing: "border-box",
  };

  return (
    <>
      <style>{`
        .feedback-dialog {
          inset: auto 24px 84px auto;
          margin: 0;
          width: 380px;
          max-width: calc(100vw - 32px);
          max-height: calc(100dvh - 108px);
          transform-origin: bottom right;
          z-index: 9200;
        }
      `}</style>

      {/* ── Tooltip ── */}
      {!open && showTip && (
        <div
          style={{
            position: "fixed",
            bottom: 114,
            right: 28,
            zIndex: 9099,
            background: "var(--text)",
            color: "#fff",
            fontSize: 11,
            fontWeight: 500,
            fontFamily: "var(--sans)",
            padding: "5px 10px",
            borderRadius: 6,
            whiteSpace: "nowrap",
            pointerEvents: "none",
          }}
        >
          Contact us · Feedback
          <div
            style={{
              position: "absolute",
              bottom: -4,
              right: 16,
              width: 8,
              height: 8,
              background: "var(--text)",
              transform: "rotate(45deg)",
            }}
          />
        </div>
      )}

      {/* ── FAB ── */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
      <button
        onMouseEnter={() => setShowTip(true)}
        onMouseLeave={() => setShowTip(false)}
        aria-label="Contact us / send feedback"
        style={{
          position: "fixed",
          bottom: 56,
          right: 24,
          zIndex: 9100,
          width: 50,
          height: 50,
          borderRadius: "50%",
          background: "var(--accent)",
          border: "none",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "var(--shadow-sm)",
          transition: "transform 0.18s",
        }}
        onMouseDown={(e) => {
          e.currentTarget.style.transform = "scale(0.94)";
        }}
        onMouseUp={(e) => {
          e.currentTarget.style.transform = "scale(1)";
        }}
      >
        {open ? (
          <svg
            width="18"
            height="18"
            fill="none"
            stroke="#fff"
            strokeWidth="2.5"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        ) : (
          <svg
            width="20"
            height="20"
            fill="none"
            stroke="#fff"
            strokeWidth="1.8"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
        )}
      </button>

        </DialogTrigger>
        <DialogContent
          className="feedback-dialog"
          hideOverlay
          title={
            status === "sent" ? "Message sent!" : "Contact Us / Send Feedback"
          }
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            firstRef.current?.focus();
          }}
        >

        {/* ── Success state ── */}
        {status === "sent" ? (
          <div style={{ padding: "36px 28px", textAlign: "center" }}>
            <div
              style={{
                width: 52,
                height: 52,
                borderRadius: "50%",
                background: "var(--green-dim)",
                border: "1px solid var(--green-border)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 16px",
              }}
            >
              <svg
                width="24"
                height="24"
                fill="none"
                stroke="var(--green)"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <div
              style={{
                fontSize: 16,
                fontWeight: 600,
                color: "var(--text)",
                marginBottom: 8,
              }}
            >
              Message sent!
            </div>
            <div
              style={{
                fontSize: 13,
                color: "var(--text-muted)",
                lineHeight: 1.6,
                maxWidth: 280,
                margin: "0 auto 22px",
              }}
            >
              Thanks for reaching out. A member of our team will get back to you
              at <strong>{email}</strong> shortly.
            </div>
            <button
              onClick={handleDone}
              style={{
                padding: "9px 24px",
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 600,
                background: "var(--accent)",
                color: "#fff",
                border: "none",
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              Done
            </button>
          </div>
        ) : (
          /* ── Form ── */
          <div style={{ padding: "18px 20px 20px" }}>
            {/* Type pills */}
            <div style={{ marginBottom: 15 }}>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: "var(--text-faint)",
                  marginBottom: 7,
                }}
              >
                Type
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {FEEDBACK_TYPES.map((ft) => (
                  <button
                    key={ft.value}
                    onClick={() => setType(ft.value)}
                    style={{
                      padding: "4px 12px",
                      borderRadius: 9999,
                      fontSize: 11.5,
                      fontWeight: 500,
                      cursor: "pointer",
                      fontFamily: "inherit",
                      transition: "all 0.13s",
                      background: type === ft.value ? "var(--accent)" : "transparent",
                      color: type === ft.value ? "#fff" : "var(--text-muted)",
                      border:
                        type === ft.value
                          ? "1px solid var(--accent)"
                          : "1px solid var(--border)",
                    }}
                  >
                    {ft.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Name + Email */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 10,
                marginBottom: 12,
              }}
            >
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: 10,
                    fontWeight: 600,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    color: "var(--text-faint)",
                    marginBottom: 5,
                  }}
                >
                  Name
                </label>
                <Input
                  label="Name"
                  hideLabel
                  ref={firstRef}
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                />
              </div>
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: 10,
                    fontWeight: 600,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    color: "var(--text-faint)",
                    marginBottom: 5,
                  }}
                >
                  Email
                </label>
                <Input
                  label="Email"
                  hideLabel
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                />
              </div>
            </div>

            {/* Message */}
            <div style={{ marginBottom: 16 }}>
              <label
                style={{
                  display: "block",
                  fontSize: 10,
                  fontWeight: 600,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: "var(--text-faint)",
                  marginBottom: 5,
                }}
              >
                Message
              </label>
              <Textarea
                label="Message"
                hideLabel
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={PLACEHOLDERS[type]}
                rows={4}
                style={{ resize: "vertical", minHeight: 95 }}
              />
            </div>

            {/* Error */}
            {status === "error" && (
              <div
                style={{
                  marginBottom: 12,
                  padding: "9px 12px",
                  borderRadius: 8,
                  background: "var(--red-dim)",
                  border: "1px solid var(--red-border)",
                  fontSize: 12.5,
                  color: "var(--red)",
                }}
              >
                Something went wrong. Email us directly at{" "}
                <strong>support@axosignal.io</strong>.
              </div>
            )}

            {/* Footer */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
              }}
            >
              <span style={{ fontSize: 11, color: "var(--text-faint)" }}>
                Or email{" "}
                <a
                  href="mailto:support@axosignal.io"
                  style={{
                    color: "var(--accent)",
                    textDecoration: "none",
                    fontWeight: 600,
                  }}
                >
                  support@axosignal.io
                </a>
              </span>
              <button
                onClick={handleSubmit}
                disabled={!canSubmit || status === "sending"}
                style={{
                  padding: "8px 18px",
                  borderRadius: 8,
                  fontSize: 13,
                  fontWeight: 600,
                  fontFamily: "inherit",
                  border: "none",
                  cursor: canSubmit ? "pointer" : "not-allowed",
                  background: canSubmit ? "var(--accent)" : "var(--border-strong)",
                  color: canSubmit ? "#fff" : "var(--text-faint)",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  transition: "background 0.15s",
                }}
              >
                {status === "sending" ? (
                  <>
                    <svg
                      width="13"
                      height="13"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      viewBox="0 0 24 24"
                      style={{ animation: "fbspin 0.8s linear infinite" }}
                    >
                      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                    </svg>
                    Sending…
                  </>
                ) : (
                  <>
                    Send Message
                    <svg
                      width="12"
                      height="12"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M22 2L11 13M22 2L15 22l-4-9-9-4 20-7z"
                      />
                    </svg>
                  </>
                )}
              </button>
            </div>
          </div>
        )}

          <style>{`@keyframes fbspin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
        </DialogContent>
      </Dialog>
    </>
  );
}
